import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { LogOut, Factory, Key, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../utils/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await apiClient.put('/users/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Define available dashboards + their sub-sections based on role hierarchy.
  // Sub-section keys map to each dashboard page's `activeTab` state, propagated via
  // router `location.state.tab`.
  const DASHBOARD_SUBTABS = {
    '/admin': [
      { key: 'users', label: 'Users' },
      { key: 'customers', label: 'Customers' },
      { key: 'orders', label: 'Orders' },
      { key: 'masters', label: 'Masters' },
      { key: 'opening-balance', label: 'Opening Balance' },
    ],
    '/manager': [
      { key: 'jobsheet', label: 'Job Sheet' },
      { key: 'bsorder', label: 'BS Order' },
      { key: 'production', label: 'Production' },
      { key: 'invoices', label: 'Invoices' },
      { key: 'stock', label: 'Stock' },
      { key: 'reports', label: 'Reports' },
    ],
    '/operator': [
      { key: 'orders', label: 'Orders' },
    ],
  };

  const getDashboards = () => {
    const dashboards = [];
    if (user?.role === 'admin') {
      dashboards.push(
        { path: '/admin', label: 'Admin' },
        { path: '/manager', label: 'Manager' },
        { path: '/operator', label: 'Operator' },
      );
    } else if (user?.role === 'manager') {
      dashboards.push(
        { path: '/manager', label: 'Manager' },
        { path: '/operator', label: 'Operator' },
      );
    } else if (user?.role === 'operator') {
      dashboards.push({ path: '/operator', label: 'Operator' });
    }
    return dashboards;
  };

  const dashboards = getDashboards();

  return (
    <>
      <nav data-testid="navbar" className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Factory className="h-7 w-7 text-slate-900" />
              <h1 className="text-2xl font-primary font-bold text-slate-900">Adroit Gravure</h1>
            </div>
            
            {dashboards.length >= 1 && (
              <div className="flex items-center gap-2 ml-8">
                {dashboards.map((dashboard) => {
                  const isActive = location.pathname === dashboard.path;
                  const subTabs = DASHBOARD_SUBTABS[dashboard.path] || [];
                  return (
                    <DropdownMenu key={dashboard.path}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant={isActive ? 'default' : 'ghost'}
                          size="sm"
                          className="flex items-center gap-1"
                          data-testid={`nav-${dashboard.label.toLowerCase()}-btn`}
                        >
                          {dashboard.label}
                          <ChevronDown className="h-4 w-4 opacity-70" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-48 bg-white border border-slate-200 shadow-lg">
                        {subTabs.map((st) => (
                          <DropdownMenuItem
                            key={st.key}
                            onSelect={() => navigate(dashboard.path, { state: { tab: st.key } })}
                            data-testid={`nav-${dashboard.label.toLowerCase()}-item-${st.key}`}
                            className="cursor-pointer bg-white hover:bg-slate-100 focus:bg-slate-100"
                          >
                            {st.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">{user?.role}</p>
            </div>
            <Button
              data-testid="change-password-button"
              onClick={() => setShowPasswordModal(true)}
              variant="ghost"
              size="sm"
              className="flex items-center gap-2"
              title="Change Password"
            >
              <Key className="h-4 w-4" />
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label>Current Password *</Label>
              <Input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label>New Password *</Label>
              <Input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                minLength={6}
                className="mt-2 h-11"
              />
            </div>
            <div>
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                className="mt-2 h-11"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changingPassword}
                className="flex-1"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Navbar;
